import { useMemo } from 'react';
import { useRegistry } from '../../registry/hooks';
import type { Device, Entity } from '../../registry/types';

export function useEntity(entityId: string | undefined): Entity | undefined {
  const { state } = useRegistry();
  return useMemo(() => {
    if (!entityId) return undefined;
    return state.entities.find((e) => e.entity_id === entityId);
  }, [state.entities, entityId]);
}

function looksLikeDeviceIdSuffix(name: string): boolean {
  // e.g. "Shelly1minig3 E4b323244c40" — trailing hex id
  return /\s[a-f0-9]{10,}$/i.test(name.trim()) || /\s[a-f0-9]{4}([a-f0-9]{4,})?$/i.test(name.trim()) && name.length > 20;
}


function stripLoxonePrefix(name: string): string {
  // HA-Sortier-Prefixes wie "0. Aufzug", "1. EG" nur wenn sie am Wortanfang stehen.
  // Erste Zahl + Punkt strippen wenn danach Text folgt.
  return name.replace(/^\d+\.\s+/, '').trim();
}

function stripDeviceIdSuffix(name: string): string {
  return name.replace(/\s+[a-f0-9]{8,}(_\d+)?$/i, '').trim();
}

export function useEntityFriendlyName(entityId: string | undefined, override?: string): string {
  const { state } = useRegistry();
  const entity = useEntity(entityId);
  return useMemo(() => {
    if (override) return override;
    const attrs = entity?.attributes as { friendly_name?: unknown } | undefined;
    if (typeof attrs?.friendly_name === 'string' && attrs.friendly_name.trim().length > 0) {
      const fn = attrs.friendly_name.trim();
      return stripLoxonePrefix(looksLikeDeviceIdSuffix(fn) ? stripDeviceIdSuffix(fn) : fn);
    }
    // Fallback 1: device.name
    if (entity?.device_id) {
      const dev: Device | undefined = state.devices.find((d) => d.device_id === entity.device_id);
      if (dev?.name && !looksLikeDeviceIdSuffix(dev.name)) return dev.name;
    }
    // Fallback 2: title-case aus entity_id ohne device_id-suffix
    const id = entity?.entity_id;
    if (!id) return 'Unbekannt';
    const slug = id.split('.', 2)[1] ?? id;
    const stripped = stripDeviceIdSuffix(slug.split('_').map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w)).join(' '));
    return stripped || 'Unbekannt';
  }, [entity, override, state.devices]);
}

// Legacy helper — direct call without hooks
export function friendlyName(entity: Entity | undefined, override?: string): string {
  if (override) return override;
  const attrs = entity?.attributes as { friendly_name?: unknown } | undefined;
  if (typeof attrs?.friendly_name === 'string' && attrs.friendly_name.trim().length > 0) {
    const fn = attrs.friendly_name.trim();
    return stripLoxonePrefix(looksLikeDeviceIdSuffix(fn) ? stripDeviceIdSuffix(fn) : fn);
  }
  const id = entity?.entity_id;
  if (!id) return 'Unbekannt';
  const slug = id.split('.', 2)[1] ?? id;
  return stripDeviceIdSuffix(slug.split('_').map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w)).join(' ')) || 'Unbekannt';
}

export function entityIcon(entity: Entity | undefined): string | undefined {
  const attrs = entity?.attributes as { icon?: unknown } | undefined;
  if (typeof attrs?.icon === 'string' && attrs.icon.startsWith('mdi:')) return attrs.icon;
  const domain = entity?.entity_id?.split('.', 1)[0];
  const map: Record<string, string> = {
    light: 'mdi:lightbulb',
    switch: 'mdi:toggle-switch',
    sensor: 'mdi:gauge',
    binary_sensor: 'mdi:motion-sensor',
    cover: 'mdi:window-shutter',
    climate: 'mdi:thermometer',
    media_player: 'mdi:cast',
    camera: 'mdi:cctv',
  };
  return domain ? map[domain] : undefined;
}

// German state semantics
export function humanState(entity: Entity | undefined): { text: string; tone: 'on' | 'off' | 'warn' | 'muted' } {
  if (!entity) return { text: '—', tone: 'muted' };
  const domain = entity.entity_id.split('.', 1)[0];
  const state = entity.state;
  const attrs = entity.attributes as Record<string, unknown>;
  const deviceClass = typeof attrs.device_class === 'string' ? attrs.device_class : '';

  if (domain === 'button') {
    // HA-Buttons: state is ISO-timestamp of last press, or 'unknown' if never pressed.
    if (state === 'unavailable') return { text: 'Offline', tone: 'muted' };
    if (state === 'unknown') return { text: 'Bereit', tone: 'muted' };
    // last-pressed timestamp — compact relative label
    const t = Date.parse(state);
    if (!Number.isNaN(t)) {
      const mins = Math.round((Date.now() - t) / 60000);
      if (mins < 1) return { text: 'Gerade eben', tone: 'off' };
      if (mins < 60) return { text: `vor ${mins} Min.`, tone: 'off' };
      const hours = Math.round(mins / 60);
      if (hours < 24) return { text: `vor ${hours} Std.`, tone: 'off' };
      const days = Math.round(hours / 24);
      return { text: `vor ${days} Tg.`, tone: 'muted' };
    }
    return { text: 'Bereit', tone: 'muted' };
  }
  if (domain === 'lock') {
    if (state === 'unavailable' || state === 'unknown') return { text: 'Offline', tone: 'muted' };
    if (state === 'locked') return { text: 'Zu', tone: 'off' };
    if (state === 'unlocked') return { text: 'Auf', tone: 'warn' };
    return { text: state, tone: 'muted' };
  }
  if (state === 'unavailable' || state === 'unknown') return { text: 'Offline', tone: 'muted' };

  if (domain === 'binary_sensor') {
    const dc = deviceClass;
    if (dc === 'motion' || dc === 'occupancy' || dc === 'presence') return state === 'on' ? { text: 'Bewegung', tone: 'warn' } : { text: 'Ruhig', tone: 'muted' };
    if (dc === 'door' || dc === 'garage_door') return state === 'on' ? { text: 'Offen', tone: 'warn' } : { text: 'Geschlossen', tone: 'off' };
    if (dc === 'window') return state === 'on' ? { text: 'Offen', tone: 'warn' } : { text: 'Geschlossen', tone: 'off' };
    if (dc === 'moisture') return state === 'on' ? { text: 'Nass', tone: 'warn' } : { text: 'Trocken', tone: 'muted' };
    if (dc === 'smoke') return state === 'on' ? { text: 'Rauch!', tone: 'warn' } : { text: 'OK', tone: 'off' };
    if (dc === 'battery') return state === 'on' ? { text: 'Schwach', tone: 'warn' } : { text: 'OK', tone: 'off' };
    return state === 'on' ? { text: 'Aktiv', tone: 'warn' } : { text: 'Ruhig', tone: 'muted' };
  }

  if (domain === 'light' || domain === 'switch') return state === 'on' ? { text: 'An', tone: 'on' } : { text: 'Aus', tone: 'off' };

  if (domain === 'cover') {
    if (state === 'open') return { text: 'Offen', tone: 'on' };
    if (state === 'closed') return { text: 'Geschlossen', tone: 'off' };
    if (state === 'opening') return { text: 'Öffnet…', tone: 'warn' };
    if (state === 'closing') return { text: 'Schließt…', tone: 'warn' };
    return { text: state, tone: 'muted' };
  }

  if (domain === 'climate') {
    const temp = attrs.current_temperature;
    if (typeof temp === 'number') return { text: `${temp.toFixed(1).replace('.', ',')} °C`, tone: 'on' };
    return { text: state, tone: 'muted' };
  }

  if (domain === 'sensor') {
    const unit = typeof attrs.unit_of_measurement === 'string' ? attrs.unit_of_measurement : '';
    const num = Number(state);
    if (Number.isFinite(num)) {
      const formatted = num % 1 === 0 ? num.toString() : num.toFixed(1).replace('.', ',');
      return { text: unit ? `${formatted} ${unit}` : formatted, tone: 'on' };
    }
    return { text: state, tone: 'muted' };
  }

  if (domain === 'media_player') {
    if (state === 'playing') return { text: 'Läuft', tone: 'on' };
    if (state === 'paused') return { text: 'Pause', tone: 'muted' };
    if (state === 'idle' || state === 'off') return { text: 'Aus', tone: 'off' };
    return { text: state, tone: 'muted' };
  }

  return { text: state, tone: 'muted' };
}

export function attrString(entity: Entity | undefined, key: string): string | undefined {
  const value = entity?.attributes?.[key];
  return typeof value === 'string' ? value : undefined;
}

export function attrNumber(entity: Entity | undefined, key: string): number | undefined {
  const value = entity?.attributes?.[key];
  return typeof value === 'number' ? value : undefined;
}
